<template lang="pug">
.marketing-task-container(
  v-if="moduleData.taskCouponList && moduleData.taskCouponList.length"
  :class="{ 'task-inline': inline, 'task-highlight': inline && task.isHighlight }"
)
  .header(v-if="!inline")
    .title {{ moduleData.mainTitle }}

  .content(@click="handleClick")
    .content-task-info
      .task-info-text
        SafeImg.task-info-img(:src="task.taskImgUrl")
        .task-info-text-main
          span.task-info-text-title {{ task.guideDesc }}{{ task.guideAwardDesc }}
          .task-info-text-amount
            img.task-info-text-amount-img(
                src="https://m.hellobike.com/fd018348_new_coin.png"
                alt=""
              )
            span.task-info-text-amount-coin +{{ task.amount }}
            span.task-subtitle {{ task.additionalDesc }}
          .task-info-text-subtitle(v-if="task.taskStatus !== 'INIT'")
            .task-info-progress
              .task-info-progress-bar(:style="{ width: progressWidth }")
            span.task-progress-text {{ progressBaseText }}
            span.task-progress-time(
              v-if="progressTimeText && task.taskStatus !== 'INIT'"
            ) {{ progressTimeText }}
      .task-info-btn(
        :class="{ 'done': task.taskStatus === 'FINISHED','can-do': task.taskStatus === 'RUNNING'}"
      )
        span.task-info-btn-text {{ task.btnName }}
        .task-end-time(
          v-if="countdown.show && task.currentProgress === task.totalProgress"
        ) {{ countdownText }}
</template>

<script>
import SafeImg from '@/components/safe-img';
import { handleTaskBtnClick } from '../../common/task';
import SPM from '../../common/spm';

export default {
  name: 'MarketingTask',
  components: { SafeImg },
  props: {
    moduleData: {
      type: Object,
      default: () => ({}),
    },
    // inline 模式：只渲染任务项内容，不渲染外层容器和标题
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // 内部倒计时状态
      countdown: {
        show: false,
        days: 0,
        hours: '00',
        minutes: '00',
        seconds: '00',
      },
      countdownTimer: null,
    };
  },
  computed: {
    // 从 moduleData 中获取任务数据
    task() {
      const list = this.moduleData.taskCouponList;
      return (list && list[0]) || {};
    },
    // 进度条宽度百分比
    progressWidth() {
      const current = Number(this.task.currentProgress) || 0;
      const total = Number(this.task.totalProgress) || 1;
      const percent = Math.min((current / total) * 100, 100);
      return `${percent}%`;
    },
    // 进度基础文本
    progressBaseText() {
      const { currentProgress, totalProgress } = this.task;
      if (currentProgress === totalProgress) {
        return `${currentProgress}/${totalProgress} 已完成！`;
      }
      return `${currentProgress}/${totalProgress}`;
    },
    // 倒计时格式化文本（纯格式化，不含业务条件判断）
    countdownText() {
      if (!this.countdown.show) return '';
      return this.countdown.days > 0
        ? `剩${this.countdown.days}天`
        : `剩${this.countdown.hours}:${this.countdown.minutes}:${this.countdown.seconds}`;
    },
    // 进度倒计时文本（未完成时展示）
    progressTimeText() {
      const { currentProgress, totalProgress } = this.task;
      if (currentProgress === totalProgress) {
        return '';
      }
      return this.countdownText;
    },
  },
  watch: {
    // 监听 moduleData 变化，重新启动倒计时
    'moduleData.taskCouponList': {
      handler() {
        this.initCountdown();
      },
      deep: true,
    },
  },
  mounted() {
    // 初始化倒计时
    this.initCountdown();
    // 多单任务模块曝光埋点
    SPM.moduleExpose({
      moduleId: 'platform_multipleTasks',
      contentId: 'platform_multipleTasks',
      businessInfo: {
        orderFrequency: String(this.task.currentProgress || 0),
        taskId: this.task.taskCode || '',
        taskname: this.task.mainTitle || this.task.taskName || '',
        task_status: this.task.taskStatus || '',
        btn_name: this.task.btnName || '',
        clk_url: (this.task.btnLink && this.task.btnLink.app) || this.task.titleBarUrl || '',
      },
    });
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    // 初始化倒计时
    initCountdown() {
      const taskItem = this.task;
      if (!taskItem) return;
      let countdownTarget;
      if (taskItem.taskStatus === 'FINISHED') {
        const ts = new Date(taskItem.offerExpireDate).getTime();
        countdownTarget = Number.isFinite(ts) ? ts : null;
      } else {
        countdownTarget = taskItem.expireTimestamp;
      }
      if (countdownTarget) {
        this.startCountdown(countdownTarget);
      }
    },
    // 启动倒计时（从 task-wrapper 迁移）
    startCountdown(expireTimestamp) {
      if (!expireTimestamp) return;
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      this.updateCountdown(expireTimestamp);
      this.countdownTimer = setInterval(() => {
        this.updateCountdown(expireTimestamp);
      }, 1000);
    },
    // 更新倒计时（从 task-wrapper 迁移）
    updateCountdown(expireTimestamp) {
      const diff = expireTimestamp - Date.now();
      if (diff <= 0) {
        this.countdown.show = false;
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.$emit('expired');
        return;
      }
      // 实际天数（向下取整），用于判断是否展示天数还是时分秒
      const rawDays = Math.floor(diff / (1000 * 60 * 60 * 24));
      // 展示天数（向上取整），如6天1小时展示"剩7天"
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor(
        (diff % (1000 * 60 * 60)) / (1000 * 60),
      );
      const seconds = Math.floor(
        (diff % (1000 * 60)) / 1000,
      );
      this.countdown = {
        show: true,
        // 不足24小时时 rawDays=0，走时分秒；>=24小时时用向上取整的 days
        days: rawDays > 0 ? days : 0,
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0'),
      };
    },
    async handleClick() {
      // 多单任务点击埋点
      SPM.clickButton({
        buttonName: 'platform_multipleTasks',
        businessInfo: {
          orderFrequency: String(this.task.currentProgress || 0),
          taskId: this.task.taskCode || '',
          taskname: this.task.mainTitle || this.task.taskName || '',
          task_status: String(this.task.taskStatus || ''),
          btn_name: this.task.btnName || '',
          clk_url: (this.task.btnLink && this.task.btnLink.app) || this.task.titleBarUrl || '',
        },
      });
      const options = {
        scene: 'task_list',
        needReceiveCoupon: true,
      };
      const { receiveSuc } = await handleTaskBtnClick.call(
        this, this.task, options,
      );
      if (receiveSuc) {
        this.$emit('expired');
      }
    },
  },
};
</script>

<style lang="less" scoped>
* {
  box-sizing: border-box;
}

.marketing-task-container {
  background-color: #fff;
  border-radius: 0.16rem;
  padding: 0.16rem 0.12rem 0.14rem;
  overflow: hidden;

  // inline 模式：去掉外层容器样式，融入父级列表
  &.task-inline {
    background-color: transparent;
    border-radius: 0;
    padding: 0.12rem;
    overflow: visible;
  }

  // inline 模式下的高亮背景，与常规任务 task-item-surprised 一致
  &.task-highlight {
    background:
      linear-gradient(
        90deg,
        rgba(255, 232, 136, 0.3) 0%,
        rgba(255, 255, 255, 0.3) 100%
      );
  }

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 0.08rem;

    .title {
      font-size: 0.15rem;
      font-weight: 600;
      color: #242729;
      line-height: 0.21rem;
    }
  }

  .content {
    display: flex;
    flex-direction: column;

    .content-task-info {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .task-info-text {
        display: flex;
        flex-direction: row;

        .task-info-img {
          width: 0.4rem;
          height: 0.4rem;
          border-radius: 0.12rem;
          object-fit: contain;
        }

        .task-info-text-main {
          display: flex;
          flex-direction: column;
          margin-left: 0.1rem;

          .task-info-text-title {
            color: #242729;
            font-size: 0.14rem;
            white-space: nowrap;
            font-weight: 500;
            max-width: 1.84rem;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .task-info-text-subtitle {
            display: flex;
            flex-direction: row;
            align-items: center;
            font-size: 0.12rem;
            margin-top: 0.04rem;

            .task-info-progress {
              width: 0.4rem;
              height: 0.04rem;
              background-color: #ebeae8;
              border-radius: 0.1942rem;
              overflow: hidden;
              margin-right: 0.04rem;

              .task-info-progress-bar {
                height: 100%;
                background-color: #fe852a;
                border-radius: 0.1942rem;
                transition: width 0.3s ease;
              }
            }

            .task-progress-text {
              color: rgba(90, 96, 102, 0.8);
              font-size: 0.12rem;
              font-weight: 400;
            }

            .task-progress-time {
              color: rgba(90, 96, 102, 0.8);
              font-size: 0.12rem;
              font-weight: 400;
              margin-left: 0.06rem;
            }
          }

          .task-info-text-amount {
            display: flex;
            align-items: center;
            margin-top: 0.04rem;

            .task-info-text-amount-img {
              width: 0.16rem;
              height: 0.16rem;
            }

            .task-info-text-amount-coin {
              color: #fe852a;
              font-weight: 600;
              font-size: 0.12rem;
              margin-left: 0.02rem;
            }

            .task-subtitle {
              max-width: 1.6rem;
              margin-left: 0.08rem;
              color: rgba(90, 96, 102, 0.8);
              font-size: 0.12rem;
              font-family: PingFangSC-Regular, PingFang SC, sans-serif;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
      }

      .task-info-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #ffe888;
        width: 0.62rem;
        height: 0.28rem;
        border-radius: 1rem;
        position: relative;
        color: #000;

        .task-info-btn-text {
          font-weight: 500;
          font-size: 0.12rem;
          line-height: 0.28rem;
        }

        .task-end-time {
          padding-left: 0.03rem;
          padding-right: 0.03rem;
          position: absolute;
          top: -0.12rem;
          right: 0;
          background-color: #fff1f0;
          white-space: nowrap;
          height: 0.16rem;
          color: #ff2919;
          font-size: 0.11rem;
          line-height: 0.16rem;
          text-align: center;
          border-radius: 0.04rem;
        }
      }

      .done {
        background: #fe852a;
        color: #fff;
      }

      .can-do {
        background: #fff;
        color: #fb750f;
        border: 0.01rem solid #ff8000;
      }
    }
  }
}
</style>
